import { Box, Typography } from "@outschool/backpack";
import { CurrencyCode } from "@outschool/gql-backend-generated";
import {
  ActivityEnrollQueryQuery,
  CurrentUserPaymentMethodsQuery,
  EnrollmentMeetingPurchaseQueryQuery,
} from "@outschool/gql-frontend-generated";
import {
  USD_STATIC_EXCHANGE_RATE,
  useCurrencyLocalization,
  useTranslation,
} from "@outschool/localization";
import { Checkbox, Loading } from "@outschool/ui-legacy-component-library";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import React from "react";

import { usePaymentMethods } from "../../hooks/CurrentUser";
import useDebouncedCallback from "../../hooks/lib/useDebouncedCallback";
import { shouldChargeInLocalCurrency } from "../../lib/Localization";
import { useExchangeRate } from "../../queries/ExchangeRateQueries";
import PriceCreditsOutstandingBalanceBreakdown from "../enrolling/enrollmentForm/PriceCreditsOutstandingBalanceBreakdown";
import { StripeProvider, useStripePayment } from "./Stripe";
import StripeAuthorization, {
  BulkPurchaseRecurringPaymentTypes,
} from "./StripeAuthorization";
import {
  StripePayMessage,
  StripeProcessingMessage,
  StripeSuccessMessage,
} from "./StripeMessages";
import StripeNewCard from "./StripeNewCard";
import { SavedPaymentMethodSelector } from "./StripeSavedCards";

export enum DataTestIds {
  StripeCheckoutForm = "StripeCheckoutForm",
}

interface StripeCheckoutFormProps {
  amountCents: number;
  orderSlugId: string;
  payingUser: {
    name?: string;
    email?: string;
  };
  isGuestCheckout?: boolean;
  onCancel: () => void;
  onSubmit?: () => void;
  onStripePaymentAccepted: (paymentIntent: string) => Promise<void>;
  charterSchoolUid?: string;
  isSavedPaymentMethodRequired?: boolean;
  isSavedPaymentMethodAllowed?: boolean;
  chargeInLocalCurrency?: boolean;
  activity?: // @ts-ignore TS(2339): Property 'activity' does not exist on type '{ __ty... Remove this comment to see the full error message
  | ActivityEnrollQueryQuery["enrollmentForm"]["activity"]
    // @ts-ignore TS(2339): Property 'activity' does not exist on type '{ __ty... Remove this comment to see the full error message
    | EnrollmentMeetingPurchaseQueryQuery["enrollmentMeetingPurchaseForm"]["activity"];
  isWeeklyPayment?: boolean;
  bulkPurchaseRecurringPaymentMethod?: BulkPurchaseRecurringPaymentTypes;
  isPayingOutstandingCreditsBalance?: boolean;
  isThreeStepBooking?: boolean;
  durationWeeks?: number | null;
  header?: React.ReactNode;
  preventSubmit?: boolean;
  useStickyFooter?: boolean;
}

export function StripeCheckoutForm(props: StripeCheckoutFormProps) {
  const { amountCents, chargeInLocalCurrency } = props;

  return (
    <StripeProvider
      amountInCents={amountCents}
      chargeInLocalCurrency={chargeInLocalCurrency}
    >
      <CheckoutForm {...props} />
    </StripeProvider>
  );
}

function CheckoutForm({
  amountCents,
  payingUser,
  isGuestCheckout,
  onCancel,
  onSubmit,
  onStripePaymentAccepted,
  isSavedPaymentMethodRequired,
  isSavedPaymentMethodAllowed = true,
  orderSlugId,
  charterSchoolUid,
  chargeInLocalCurrency,
  activity,
  isWeeklyPayment,
  bulkPurchaseRecurringPaymentMethod,
  isPayingOutstandingCreditsBalance = false,
  isThreeStepBooking = false,
  durationWeeks,
  preventSubmit,
  header,
}: StripeCheckoutFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const { loading: isPaymentMethodsLoading, paymentMethods } =
    usePaymentMethods();

  const defaultPaymentMethod = paymentMethods.find(
    (
      paymentMethod: NonNullable<
        CurrentUserPaymentMethodsQuery["currentUser"]
      >["paymentMethods"][number],
    ) => paymentMethod.isCustomerDefault,
  );

  const [selectedPaymentMethod, setSelectedPaymentMethod] = React.useState<
    | NonNullable<
        CurrentUserPaymentMethodsQuery["currentUser"]
      >["paymentMethods"][number]
    | null
  >(null);
  const [isUsingSavedPaymentMethod, setIsUsingSavedPaymentMethod] =
    React.useState(true);
  const [isSaveForLater, setIsSaveForLater] = React.useState(false);

  const { t } = useTranslation(
    "client\\components\\payments\\StripeCheckoutForm",
  );

  const { currencyCode } = useCurrencyLocalization();
  const { exchangeRate } = useExchangeRate(currencyCode);

  const isChargingInLocalCurrency =
    shouldChargeInLocalCurrency(currencyCode) && chargeInLocalCurrency;

  const {
    isPaymentProcessing,
    isPaymentSuccessful,
    isCardComplete,
    error: paymentError,
    setIsCardComplete,
    setBillingDetails,
    payWithStripe,
  } = useStripePayment({
    orderSlugId,
    amountCents,
    onStripePaymentAccepted,
    charterSchoolUid,
    purchaserEmail: payingUser?.email,
    currencyCode: isChargingInLocalCurrency ? currencyCode : CurrencyCode.Usd,
    exchangeRate: isChargingInLocalCurrency
      ? exchangeRate
      : USD_STATIC_EXCHANGE_RATE,
  });

  const loggedOutError = t("Please sign in to purchase a gift card.");

  const hasPayment = isUsingSavedPaymentMethod || isCardComplete;
  const disableSubmit =
    !stripe ||
    isPaymentProcessing ||
    !hasPayment ||
    isPaymentSuccessful ||
    (isSavedPaymentMethodRequired && !isSaveForLater) ||
    preventSubmit ||
    isGuestCheckout;
  const showSavedCards =
    !isGuestCheckout &&
    isSavedPaymentMethodAllowed &&
    isUsingSavedPaymentMethod;

  React.useEffect(() => {
    setBillingDetails({ name: payingUser.name });
  }, [setBillingDetails, payingUser]);
  const willSavePaymentMethod = isSavedPaymentMethodRequired || isSaveForLater;

  React.useEffect(() => {
    if (paymentMethods?.length > 0) {
      setSelectedPaymentMethod(defaultPaymentMethod || paymentMethods[0]);
    }
  }, [setSelectedPaymentMethod, paymentMethods, defaultPaymentMethod]);

  const handleSubmit = useDebouncedCallback<unknown, React.FormEvent>(
    async e => {
      e && e.preventDefault();
      if (disableSubmit) {
        return;
      }
      if (!!onSubmit) {
        onSubmit();
      }
      // If the amount is 0, we don't need to submit the form
      if (amountCents === 0) {
        await onStripePaymentAccepted("");
        return;
      }
      await elements?.submit();
      await payWithStripe({
        paymentMethodIdToUse: selectedPaymentMethod?.uid,
        shouldSavePaymentMethod: isSavedPaymentMethodAllowed
          ? willSavePaymentMethod
          : false,
        shouldUseSavedPaymentMethod: isSavedPaymentMethodAllowed
          ? isUsingSavedPaymentMethod
          : false,
      });
    },
    [
      isSavedPaymentMethodAllowed,
      isSaveForLater,
      isUsingSavedPaymentMethod,
      onSubmit,
      payWithStripe,
      willSavePaymentMethod,
      disableSubmit,
    ],
  );

  const handleGoBack = () => {
    if (!isUsingSavedPaymentMethod) {
      setIsUsingSavedPaymentMethod(true);
    } else {
      onCancel();
    }
  };

  const showLoading =
    !stripe || !elements || (isPaymentMethodsLoading && !defaultPaymentMethod);

  if (showLoading) {
    return <Loading />;
  }
  return (
    <Box
      flex
      sx={{
        width: "100%",
        flexDirection: "column",
      }}
      data-test-id={DataTestIds.StripeCheckoutForm}
    >
      <form onSubmit={handleSubmit}>
        <Box>
          <Box
            sx={{
              paddingTop: "0.5em",
            }}
          >
            {isPayingOutstandingCreditsBalance && (
              <PriceCreditsOutstandingBalanceBreakdown />
            )}
            <Box
              sx={{
                display:
                  isPaymentProcessing || isPaymentSuccessful ? "none" : "",
              }}
            >
              {header}
              {showSavedCards ? (
                <SavedPaymentMethodSelector
                  disabled={isPaymentProcessing || isPaymentSuccessful}
                  onFetchPaymentMethodsError={() => {
                    setSelectedPaymentMethod(null);
                    setIsUsingSavedPaymentMethod(false);
                  }}
                  onSelectPaymentMethod={setSelectedPaymentMethod}
                  onAddNewCard={() => {
                    setSelectedPaymentMethod(null);
                    setIsUsingSavedPaymentMethod(false);
                  }}
                />
              ) : (
                <Box>
                  <StripeNewCard
                    willCardBeSavedAsDefault={willSavePaymentMethod}
                    setIsCardComplete={setIsCardComplete}
                    setBillingDetails={setBillingDetails}
                    isGuestCheckout={isGuestCheckout}
                  />
                  {!isGuestCheckout &&
                    isSavedPaymentMethodAllowed &&
                    !isSavedPaymentMethodRequired && (
                      <Box
                        sx={{
                          marginBottom: "1em",
                          width: "100%",
                        }}
                      >
                        <Checkbox
                          label={
                            <Box>
                              <Typography
                                variant="inherit"
                                component="div"
                                sx={{
                                  color: "grey.500",
                                  fontWeight: 700,
                                }}
                              >
                                {t("Set this card as my default.")}
                              </Typography>
                              <Typography
                                variant="inherit"
                                component="div"
                                sx={{
                                  color: "grey.500",
                                }}
                              >
                                {t(
                                  "Changing your default card affects all active enrollments, including memberships.",
                                )}
                              </Typography>
                            </Box>
                          }
                          checked={willSavePaymentMethod}
                          disabled={isPaymentProcessing || isPaymentSuccessful}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setIsSaveForLater(e.target.checked)
                          }
                          iconSx={{ alignSelf: "baseline" }}
                        />
                      </Box>
                    )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {isPaymentProcessing ? (
          <StripeProcessingMessage />
        ) : isPaymentSuccessful ? (
          <StripeSuccessMessage />
        ) : (
          <>
            <StripeAuthorization
              setIsSaveForLater={setIsSaveForLater}
              isSaveForLater={isSaveForLater}
              isPaymentProcessing={isPaymentProcessing}
              isPaymentSuccessful={isPaymentSuccessful}
              activity={activity}
              isWeeklyPayment={isWeeklyPayment}
              bulkPurchaseRecurringPaymentMethod={
                bulkPurchaseRecurringPaymentMethod
              }
              isThreeStepBooking={isThreeStepBooking}
              durationWeeks={durationWeeks}
            />
            <StripePayMessage
              // We currently prevent logged out users from buying gift cards.
              // If you are changing this behaviour to allow logged out purchases,
              // you must 1. undo this MR and 2. remove the check in the backend:
              // https://gitlab.com/outschool-eng/outschool/-/merge_requests/19387
              // https://gitlab.com/outschool-eng/outschool/-/commit/9bcea23c8cfbd081be59f633867fe6bb1150c1a8
              paymentError={isGuestCheckout ? loggedOutError : paymentError}
              amountCents={amountCents}
              isChargingInLocalCurrency={isChargingInLocalCurrency}
              disableSubmit={disableSubmit}
              exchangeRate={exchangeRate}
              currencyCode={currencyCode}
              handleGoBack={handleGoBack}
            />
          </>
        )}
      </form>
    </Box>
  );
}
